import findUpClassName from '@/helpers/dom/findUpClassName';
import sequentialDom from '@/helpers/sequentialDom';
import IS_TOUCH_SUPPORTED from '@/environment/touchSupport';
import findUpAsChild from '@/helpers/dom/findUpAsChild';
import { fastRaf } from '@/helpers/schedulers';
import liteMode from '@/helpers/liteMode';
import { Accessor, createRenderEffect, onCleanup } from 'solid-js';
import styles from '@/components/ripple.module.scss';


declare module 'solid-js' {
  namespace JSX {
    interface Directives {
      /**
       * To be used in solid-js as `<button use:ripple />`
       */
      ripple: boolean;
    }
  }
}

// fraction of the ripple circle that is fully opaque before the gradient feathers out;
// keep in sync with the radial-gradient inner stop in ripple.module.scss
const RIPPLE_SOLID_RATIO = 0.68;

let rippleClickId = 0;
function _ripple(
  elem: HTMLElement,
  prepend: boolean | 'no' = true,
  callback: (id: number) => Promise<boolean | void> = () => Promise.resolve(),
  onEnd: (id: number) => void = null!,
  attachListenerTo = elem
) {
  if (elem.classList.contains(styles.ripple)) return;
  elem.classList.add('rp');

  const r = document.createElement('div');
  r.classList.add(styles.ripple);

  if (prepend !== 'no') {
    elem[prepend ? 'prepend' : 'append'](r);
  }

  let handler: (() => void) | null, lastHandler: typeof handler;
  // let animationEndPromise: Promise<number>;
  const drawRipple = (clientX: number, clientY: number) => {
    const startTime = Date.now();
    const circle = document.createElement('div');

    const clickId = rippleClickId++;

    // console.log('ripple drawRipple');

    // const auto = elem.classList.contains('row-sortable') && !elem.classList.contains('cant-sort');
    const auto = false;
    const duration = (auto ? .3 : +(r.ownerDocument.defaultView || window).getComputedStyle(r).getPropertyValue('--ripple-duration').replace('s', '')) * 1000;
    // console.log('ripple duration', duration);

    const _handler = handler = lastHandler = () => {
    // handler = () => animationEndPromise.then((duration) => {
      // console.log('ripple animation was:', duration);

      // const duration = isSquare || mediaSizes.isMobile ? 200 : 700;
      // return;
      const elapsedTime = Date.now() - startTime;
      const cb = () => {
        // console.log('ripple elapsedTime total pre-remove:', Date.now() - startTime);
        sequentialDom.mutate(() => {
          circle.remove();
        });

        onEnd?.(clickId);
      };
      if (elapsedTime < duration) {
        const delay = Math.max(duration - elapsedTime, duration / 2);
        setTimeout(() => circle.classList.add(styles.hiding), Math.max(delay - duration / 2, 0));

        setTimeout(cb, delay);
      } else {
        circle.classList.add(styles.hiding);
        setTimeout(cb, duration / 2);
      }

      if (!IS_TOUCH_SUPPORTED) {
        // Same window the listeners were attached to (the element's own — the Document PiP window
        // when the client is popped out), not the main `window`.
        const win = r.ownerDocument.defaultView || window;
        win.removeEventListener('contextmenu', handler!);
        win.removeEventListener('mousemove', handler!);
      }

      handler = null;
      touchStartFired = false;
    };
    // });

    callback?.(clickId);

    /* callback().then((bad) => {
      if(bad) {
        span.remove();
        return;
      } */

    // console.log('ripple after promise', Date.now() - startTime);
    // console.log('ripple tooSlow:', tooSlow);
    /* if(tooSlow) {
        span.remove();
        return;
      } */

    fastRaf(() => {
      if (lastHandler !== _handler) {
        return;
      }

      const rect = r.getBoundingClientRect();
      circle.classList.add(styles.circle);

      const clickX = clientX - rect.left;
      const clickY = clientY - rect.top;

      const radius = Math.sqrt((Math.abs(clickY - rect.height / 2) + rect.height / 2) ** 2 + (Math.abs(clickX - rect.width / 2) + rect.width / 2) ** 2);
      const size = radius / RIPPLE_SOLID_RATIO;

      // center of circle
      const x = clickX - size / 2;
      const y = clickY - size / 2;

      // console.log('ripple click', offsetFromCenter, size, clickX, clickY);

      circle.style.width = circle.style.height = size + 'px';
      circle.style.left = x + 'px';
      circle.style.top = y + 'px';
      circle.style.opacity = '0';

      // нижний код выполняется с задержкой
      /* animationEndPromise = new Promise((resolve) => {
          span.addEventListener('animationend', () => {
            // 713 -> 700
            resolve(((Date.now() - startTime) / 100 | 0) * 100);
          }, {once: true});
        }); */

      // нижний код не всегда включает анимацию ПРИ КЛИКЕ НА ТАЧПАД БЕЗ ТАПТИК ЭНЖИНА
      /* span.style.display = 'none';
        r.append(span);
        duration = +window.getComputedStyle(span).getPropertyValue('animation-duration').replace('s', '') * 1000;
        span.style.display = ''; */

      r.append(circle);

      void circle.offsetWidth; // force reflow
      circle.style.opacity = '';

      if (auto) {
        // window.addEventListener('mousemove', handler, {once: true, passive: true});
        _handler();
      }

      // r.classList.add('active');
      // handler();
    });
    // });
  };

  const isRippleUnneeded = (e: Event) => {
    return e.target !== elem && (
      ['BUTTON', 'A'].includes((e.target as HTMLElement).tagName) ||
        findUpClassName(e.target as HTMLElement, styles.ripple) !== r
    ) && (
      attachListenerTo === elem ||
        !findUpAsChild(((e.target as HTMLElement) as { parentElement: HTMLElement; }), attachListenerTo)
    ) && !findUpClassName(e.target!, 'checkbox-field');
  };

  // TODO: rename this variable
  let touchStartFired = false;
  if (IS_TOUCH_SUPPORTED) {
    const touchEnd = () => {
      handler?.();
    };

    const onTouchStart = (e: TouchEvent) => {
      if (!liteMode.isAvailable('animations')) {
        return;
      }

      // console.log('ripple touchstart', e);
      if (e.touches.length > 1 || touchStartFired || isRippleUnneeded(e)) {
        return;
      }

      // console.log('touchstart', e);
      touchStartFired = true;

      const { clientX, clientY } = e.touches[0];
      drawRipple(clientX, clientY);
      attachListenerTo.addEventListener('touchend', touchEnd, { once: true });

      window.addEventListener('touchmove', (e) => {
        e.cancelBubble = true;
        e.stopPropagation();
        touchEnd();
        attachListenerTo.removeEventListener('touchend', touchEnd);
      }, { once: true });
    };

    attachListenerTo.addEventListener('touchstart', onTouchStart, { passive: true });
    return {
      dispose: () => {
        attachListenerTo.removeEventListener('touchstart', onTouchStart);
        r.remove();
      },
      element: r,
    };
  } else {
    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) { // only left button
        return;
      }

      if (!liteMode.isAvailable('animations')) {
        return;
      }
      // console.log('ripple mousedown', e, e.target, findUpClassName(e.target as HTMLElement, 'c-ripple') === r);

      if (attachListenerTo.dataset.ripple === '0' || isRippleUnneeded(e)) {
        return;
      } else if (touchStartFired) {
        touchStartFired = false;
        return;
      }

      const { clientX, clientY } = e;
      drawRipple(clientX, clientY);
      // Attach the ripple-end listeners to the element's OWN window — in a Document PiP window the
      // mouseup fires there, not on the main `window`, so binding to main left the ripple stuck.
      const win = attachListenerTo.ownerDocument.defaultView || window;
      win.addEventListener('mouseup', handler!, { once: true, passive: true });
      win.addEventListener('contextmenu', handler!, { once: true, passive: true });
    };

    attachListenerTo.addEventListener('mousedown', onMouseDown, { passive: true });
    return {
      dispose: () => {
        attachListenerTo.removeEventListener('mousedown', onMouseDown);
        r.remove();
      },
      element: r,
    };
  }
}

export default function ripple(elem: HTMLElement, accessor?: Accessor<boolean>, prepend?: boolean | 'no') {
  if (accessor) {
    createRenderEffect(() => {
      const value = accessor();
      if (value === undefined || value) {
        const ret = _ripple(elem, prepend);
        onCleanup(() => {
          ret!.dispose();
        });
      }
    });

    return;
  }

  const ret = _ripple(elem, prepend);
  return ret;
}
